import React from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

const Metric = ({ 
  title,
  value,
  delta,
  deltaType = 'increase', // 'increase', 'decrease', 'neutral'
  format = 'number',
  prefix = '',
  suffix = '',
  size = 'medium',
  color,
  className = '',
  style = {},
  ...props 
}) => {
  const { getColor, getSpacing, getFontSize, getBorderRadius, typography } = useThemeTokens();
  
  // Formatear el valor según el tipo
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: 'COP',
          minimumFractionDigits: 0,
        }).format(val);
      case 'percentage':
        return `${val}%`;
      case 'number':
        return new Intl.NumberFormat('es-CO').format(val);
      default:
        return val;
    }
  };
  
  // Determinar color del delta
  const getDeltaColor = () => {
    switch (deltaType) {
      case 'increase':
        return getColor('accent.green');
      case 'decrease':
        return getColor('accent.orange');
      case 'neutral':
        return getColor('neutral.mediumGray');
      default:
        return getColor('neutral.mediumGray');
    }
  };
  
  // Determinar icono del delta
  const getDeltaIcon = () => {
    switch (deltaType) {
      case 'increase':
        return '↗';
      case 'decrease':
        return '↘';
      case 'neutral':
        return '→';
      default:
        return '';
    }
  };
  
  const containerStyles = {
    backgroundColor: getColor('neutral.white'),
    padding: size === 'small' ? getSpacing('md') : size === 'large' ? getSpacing('xl') : getSpacing('lg'),
    borderRadius: getBorderRadius('lg'),
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${getColor('neutral.lightGray')}`,
    textAlign: 'center',
    ...style,
  };
  
  const titleStyles = {
    fontSize: size === 'small' ? getFontSize('sm') : size === 'large' ? getFontSize('lg') : getFontSize('base'),
    fontWeight: typography.fontWeights.medium,
    color: getColor('neutral.mediumGray'),
    margin: 0,
    marginBottom: getSpacing('xs'),
    lineHeight: typography.lineHeights.normal,
  };
  
  const valueStyles = {
    fontSize: size === 'small' ? getFontSize('xl') : size === 'large' ? getFontSize('4xl') : getFontSize('3xl'),
    fontWeight: typography.fontWeights.bold,
    color: color || getColor('neutral.black'),
    margin: 0,
    marginBottom: delta ? getSpacing('xs') : 0,
    lineHeight: typography.lineHeights.tight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getSpacing('xs'),
  };
  
  const deltaStyles = {
    fontSize: size === 'small' ? getFontSize('xs') : size === 'large' ? getFontSize('base') : getFontSize('sm'),
    fontWeight: typography.fontWeights.medium,
    color: getDeltaColor(),
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: getSpacing('xs'),
  };
  
  const formattedValue = formatValue(value);
  const formattedDelta = delta ? formatValue(Math.abs(delta)) : null;
  
  return (
    <div 
      className={`gaero-metric ${className}`}
      style={containerStyles}
      {...props}
    >
      {title && (
        <div style={titleStyles}>
          {title}
        </div>
      )}
      
      <div style={valueStyles}>
        {prefix && <span className="metric-prefix">{prefix}</span>}
        <span className="metric-value">{formattedValue}</span>
        {suffix && <span className="metric-suffix">{suffix}</span>}
      </div>
      
      {delta !== null && delta !== undefined && (
        <div style={deltaStyles}>
          <span className="delta-icon">{getDeltaIcon()}</span>
          <span className="delta-value">{formattedDelta}</span>
        </div>
      )}
    </div>
  );
};

export default Metric;
